import {forwardRef, useCallback, useEffect, useMemo, useRef} from 'react';
import {ArrowDown} from 'lucide-react';
import {useIntl} from 'react-intl';
import {insightsMessages as m} from '../insightsMessages';
import {focusRing, lucide} from '../insightsUi';
import {logPlaybackSeek} from '../insightsLog';
import {usePlayback} from '../player/PlaybackContext';
import {findActiveIndex} from './activeSegment';
import type {ResolvedSegment} from './speakers';
import {TranscriptRow} from './TranscriptRow';
import {TranscriptSearchBar} from './TranscriptSearchBar';
import {useFollowPlayback} from './useFollowPlayback';
import type {TranscriptSearch} from './useTranscriptSearch';

export interface TranscriptTarget {
    index: number;
    nonce: number;
}

interface TranscriptViewProps {
    segments: readonly ResolvedSegment[];
    search: TranscriptSearch;
    target: TranscriptTarget | null;
    onExport: () => void;
}

export const TranscriptView = forwardRef<HTMLInputElement, TranscriptViewProps>(({segments, search, target, onExport}, searchRef) => {
    const intl = useIntl();
    const playback = usePlayback();
    const listRef = useRef<HTMLDivElement>(null);
    const activeIndex = playback.canSeek ? findActiveIndex(segments, playback.currentTime) : -1;
    const {following, backToCurrent, stopFollowing, scrollTo} = useFollowPlayback(listRef, activeIndex, playback.playing);

    const seek = playback.seek;
    const onSeek = useCallback((index: number, via: 'row' | 'timestamp') => {
        seek(segments[index].startTime, {play: true});
        logPlaybackSeek(via);
    }, [seek, segments]);

    const currentMatch = search.currentMatch;
    useEffect(() => {
        if (!currentMatch) return;
        stopFollowing();
        scrollTo(currentMatch.segmentIndex);
    }, [currentMatch, scrollTo, stopFollowing]);

    useEffect(() => {
        if (target) scrollTo(target.index);
    }, [target, scrollTo]);

    const emptyRanges = useMemo(() => [], []);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 px-6 pb-2 pt-1">
                <TranscriptSearchBar ref={searchRef} search={search} onExport={onExport}/>
            </div>
            <div className="relative min-h-0 flex-1">
                <div ref={listRef} className="h-full overflow-y-auto px-4 pb-24 pt-1" data-testid="insights-transcript-scroll">
                    <ol aria-label={intl.formatMessage(m.transcriptLabel)} className="m-0 flex list-none flex-col gap-1 p-0">
                        {segments.map((segment, index) => (
                            <TranscriptRow
                                key={segment.segmentId}
                                segment={segment}
                                index={index}
                                active={index === activeIndex}
                                targeted={target?.index === index}
                                canSeek={playback.canSeek}
                                ranges={search.rangesBySegment.get(index) ?? emptyRanges}
                                currentMatchStart={currentMatch?.segmentIndex === index ? currentMatch.start : null}
                                onSeek={onSeek}
                            />
                        ))}
                    </ol>
                </div>
                {!following && playback.playing && activeIndex >= 0 && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center">
                        <button
                            type="button"
                            onClick={backToCurrent}
                            className={`pointer-events-auto inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border-0 bg-inverse-surface px-3 text-footnote font-semibold text-white shadow-menu motion-safe:animate-insights-pop-in ${focusRing}`}
                        >
                            <ArrowDown size={14} aria-hidden="true" className={lucide}/>
                            {intl.formatMessage(m.backToCurrent)}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

TranscriptView.displayName = 'TranscriptView';
